import React from "react";
import {CUSTOMERS, DASHBOARD, DEPOSITS, INVOICES, PRODUCTS, REPORTS, SETTINGS, TRANSACTIONS} from "../constants/Permissions";
import {
    IconSearch,
    IconLayoutDashboard as IconDashboard,
    IconAddressBook as IconPhoneBook,
    IconPackage as IconProducts,
    IconFileDollar as IconAccountDocument,
    IconCoin as IconDeposits,
    IconArrowsLeftRight as IconTransfer,
    IconReportAnalytics,
    IconSettings as IconCog
} from '@tabler/icons-react'

import {isMacOS, isWindows} from "./browser";
import { trackClick } from "./segment";
import KeyboardShortcut from "../components/KeyboardShortcut";
export const Permissions = [
    {
        value: "dashboard",
        label: "Dashboard",
    },
    {
        value: "customers",
        label: "Customers",
    },
    {
        value: "products",
        label: "Products",
    },
    {
        value: "deposits",
        label: "Deposits",
    },
    {
        value: "invoices",
        label: "Invoices",
    },
    {
        value: "transactions",
        label: "Transactions",
    },
    {
        value: "reports",
        label: "Reports",
    },
    {
        value: "settings",
        label: "Settings",
    },
];

export const getLinks = (isDemo=false, hasScalePlan=false) => {
    return([
        {
            icon: IconSearch,
            label: "Search",
            tag: <KeyboardShortcut keys={['Cmd', 'K']}/>,
            onClick: () => {
                const event = new KeyboardEvent('keydown', {
                    key: 'k',
                    code: 'KeyK',
                    metaKey: isMacOS(), // Set metaKey to true for Cmd key on macOS
                    ctrlKey: isWindows(), // Set ctrlKey to true for Cmd key on Windows/Linux
                });

                trackClick({section: 'search_component', element: 'search_menu_button', invoked_by: 'click', action: 'open'});
                if (typeof window !== "undefined") {
                    document.dispatchEvent(event);
                }

                // for mobile, where there's no keydown even
                const searchEvent = new Event("mobile_open_search");
                if (typeof window !== "undefined") {
                    document.dispatchEvent(searchEvent);
                }
            }
        },
        {
            icon: IconDashboard,
            label: "Dashboard",
            href: "/",
            permission: DASHBOARD,
        },
        {
            icon: IconPhoneBook,
            label: "Customers",
            href: "/customers",
            permission: CUSTOMERS,
            regExp: /^\/customers(.*?)/,
        },
        {
            icon: IconProducts,
            label: "Products",
            href: "/products",
            permission: PRODUCTS,
            regExp: /^\/products(.*?)/,
        },
        {
            icon: IconDeposits,
            label: "Deposits",
            href: "/deposits",
            permission: DEPOSITS,
            regExp: /^\/deposits(.*?)/,
        },
        {
            icon: IconAccountDocument,
            label: "Invoices",
            href: "/invoices",
            permission: INVOICES,
            subMenu: [
                { label: 'Draft', href: '/invoices', regExp: /(\/invoices(\?.*)?$)|(\/draft-invoices)/, exclude: /^\/settings(.*?)/},
                { label: 'Scheduled', href: '/invoices/scheduled', regExp: /(\/invoices\/scheduled(\?.*)?$)/},
                { label: 'Sent', href: '/invoices/sent', regExp: /(\/invoices\/sent)|(\/invoices(?!(\/scheduled|\?|$))(.*)$)/},
            ],
            whitelistPaths: ['/invoices/[id]'],
            exclude: /^\/settings(.*?)/
        },
        {
            icon: IconTransfer,
            label: "Transactions",
            href: "/transactions",
            permission: TRANSACTIONS,
            regExp: /^\/transactions(.*?)/
        },
        {
            icon: IconReportAnalytics,
            label: "Reports",
            href: "/reports",
            permission: REPORTS,
            regExp: /^\/reports(.*?)/,
            subMenu: [
                { label: 'Payouts', href: '/reports', regExp: /(\/reports?(\?payout_id=([0-9]+))$)|(\/reports$)/}, // url matches either '/reports' or 'reports?=payout_id=*'
                { label: 'Aging Balance', href: '/reports/aging-balance', regExp: /\/reports\/aging-balance/ },
                { label: 'Collections', href: '/reports/collections', regExp: /\/reports\/collections/ },
                ...(hasScalePlan ? [{ label: 'QuickPay', href: '/reports/quickpay', regExp: /\/reports\/quickpay/ }] : []),
                { label: 'Product Sales', href: '/reports/product-sales', regExp: /\/reports\/product-sales/ },
                { label: 'Customer Sales', href: '/reports/customer-sales', regExp: /\/reports\/customer-sales/ },
            ]
        },
        {
            icon: IconCog,
            label: "Settings",
            href: "/settings",
            permission: SETTINGS,
            regExp: /^\/settings(.*?)/,
            subMenu: [
                // Its tabs: General, Notifications, Plans & Billing and Audit Log
                { label: 'Account', href: '/settings', regExp: /^\/settings(\/account\/(notifications|billing|audit-log))?([?#].*)?$/ },
                ... isDemo ? [] : [
                    { label: 'Payments', href: '/settings/payments', regExp: /(\/settings\/payments)/ },
                    { label: 'Checkout', href: '/settings/checkout', regExp: /(\/settings\/checkout)/ },
                ],
                { label: 'Customers', href: '/settings/customers' },
                { label: 'Invoicing', href: '/settings/invoicing', regExp: /^\/settings\/invoicing([?#].*)?$/ },
                { label: 'Collections', href: '/settings/collections', regExp: /^\/settings\/collections([?#].*)?$/ },
                { label: 'Integrations', href: '/settings/integrations', regExp: /settings\/integrations(?:\/.*)?$/ },
            ]
        },
    ])
}

// An empty permissions list means full access
export const hasPermission = (permissions, permission) => !permissions?.length || permissions.includes(permission);

export const filterLinksFromPermissions = (permissions, isDemo=false, hasScalePlan=false) => {
    // If no permissions, return all links
    if (!permissions || permissions.length === 0) {
        return getLinks(isDemo, hasScalePlan);
    }

    // Create a map for O(1) lookups instead of O(n) includes() calls
    const permissionsMap = new Set(permissions);

    // Get all links once
    const allLinks = getLinks(isDemo, hasScalePlan);

    // Filter links in a single pass
    return allLinks.flatMap(link => {
        // If the link has no permission requirement, keep it
        if (!link.permission) return [link];

        // Check if the user has the required permission
        if (permissionsMap.has(link.permission)) return [link];

        // Anyone on the team can manage their own notification emails, so a user
        // without settings access keeps a Settings entry with only that page.
        if (link.permission === SETTINGS) return [notificationsOnlyLink(link)];

        return [];
    });
};

export const NOTIFICATIONS_HREF = '/settings/account/notifications';

const notificationsOnlyLink = (settingsLink) => {
    const {permission, ...rest} = settingsLink;
    return {
        ...rest,
        href: NOTIFICATIONS_HREF,
        subMenu: [{label: 'Notifications', href: NOTIFICATIONS_HREF, regExp: /\/notifications/}],
    };
};

// Options for the team permission pickers. Deposits is a per-company feature, so
// offering its checkbox to a merchant without the feature would leak it.
export const getPermissionOptions = ({ hasDepositLinks = false } = {}) =>
    Permissions.filter((permission) => permission.value !== DEPOSITS || hasDepositLinks);

export const isCompanyShopifyDemo = ({company}) => company && company?.source === "shopify" && company?.is_demo;
export const isGoodsMerchant = (company) => company && company.is_goods_merchant;
export const isServiceMerchant = (company) => company && !company.is_goods_merchant;
